/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {
  createMsalInstance,
  initializeMsal as kitInitializeMsal,
  makeLoginRequest,
  makeTokenProvider,
  type AuthMode,
  type MsalAuthConfig,
} from '@vaas/react-ui-kit/auth';

import { config, isAuthConfigured } from '@/lib/config';
import { setAccessTokenClaims } from '@/lib/tokenClaims';

/** Entra sign-in when configured; mock session for local dev without an IdP. */
export const authMode: AuthMode = isAuthConfigured() ? 'msal' : 'mock';

const authConfig: MsalAuthConfig = {
  clientId: config.auth.clientId,
  authority: config.auth.authority,
  redirectUri: config.auth.redirectUri,
  postLogoutRedirectUri: config.auth.postLogoutRedirectUri,
  apiScopes: config.auth.apiScopes,
  debug: import.meta.env.DEV,
};

export const msalInstance = createMsalInstance(authConfig);

export const loginRequest = makeLoginRequest(authConfig);

export const tokenProvider = makeTokenProvider(msalInstance, config.auth.apiScopes);

/**
 * Remove leftover MSAL entries that `clearCache()` can miss: a stale
 * `msal.interaction.status` marker (from a redirect that never completed) or `msal.*` keys in
 * the storage backend MSAL isn't currently configured for. These are the entries that make a
 * normal browser fail to sign in while incognito (a clean slate) works.
 */
function purgeMsalStorageKeys(): void {
  // Read the storage property lazily inside the try: accessing localStorage/sessionStorage can
  // itself throw (SecurityError) in some privacy modes, so it must stay best-effort.
  for (const getStore of [() => globalThis.localStorage, () => globalThis.sessionStorage]) {
    try {
      const store = getStore();
      for (const key of Object.keys(store)) {
        if (key.startsWith('msal.')) store.removeItem(key);
      }
    } catch {
      // Storage can be unavailable (e.g. privacy mode); nothing to purge then.
    }
  }
}

/**
 * Reset all MSAL client state so the next sign-in behaves like a fresh (incognito) session.
 * No-op in mock mode. Used to recover from a corrupt/stuck cache that blocks interactive login.
 */
export async function clearMsalCache(): Promise<void> {
  if (authMode !== 'msal') return;
  try {
    await msalInstance.clearCache();
  } catch (err) {
    console.warn('[auth] msalInstance.clearCache failed; purging storage manually:', err);
  }
  purgeMsalStorageKeys();
}

// Log effective auth config at boot to surface env/registration mismatches.
console.info(
  `[auth] mode=${authMode} redirectUri=${config.auth.redirectUri} ` +
    `clientId=${config.auth.clientId || '(none)'} authority=${config.auth.authority || '(none)'}`,
);

/** No-op in mock mode so dev boots without an IdP. */
export async function initializeMsal(): Promise<void> {
  if (authMode !== 'msal') return;
  try {
    await kitInitializeMsal(msalInstance);
    // Decode access-token claims before first render for correct role gating.
    setAccessTokenClaims(await tokenProvider());
  } catch (err) {
    // Log Entra initialization failures instead of failing silently.
    console.error('[auth] MSAL initialize/handleRedirect failed:', err);
    await clearMsalCache();
  }
}
